import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Box } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { logger } from './utils/logger';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import TripList from './components/trips/TripList';
import {
  DetailsFormRoute,
  ItineraryOverviewRoute,
  PublicItineraryRoute,
} from './routes';
import Navbar from './components/layout/Navbar';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

/**
 * createBrowserRouter is a centralized routing configuration function that creates a router
 * object that is used to navigate between different routes in the application. It is a
 * replacement for the createBrowserHistory (and previously BrowserRouter) function.
 */

// Module-level guard: dynamic imports are runtime-cached so a duplicate import() call is essentially
// a no-op, but the guard suppresses the duplicate log line under Strict Mode's effect double-invoke.
// Kept narrow and intentional — investigated and retained in Phase 5d.
let loadedLocaleFor: string | null = null;

// Component that uses locale logic and app rendering (must be inside LocaleProvider)
const AppContent = () => {
  const { locale } = useLocale();

  // Convert locale string (e.g., "en-US", "fr-FR") to Mantine format (e.g., "en", "fr")
  // Mantine DatesProvider uses dayjs locales which typically use the language code only
  const getMantineLocale = (localeString: string): string => {
    // Extract the language code from locale strings like "en-US" or "fr-FR"
    // Mantine/dayjs typically expects just "en" or "fr"
    const parts = localeString.split('-');
    return parts[0] || localeString;
  };

  const mantineLocale = getMantineLocale(locale);

  // Dynamically import dayjs locale when locale changes. Module-level guard avoids duplicate run (and logs) under Strict Mode.
  useEffect(() => {
    if (loadedLocaleFor === mantineLocale) return;
    loadedLocaleFor = mantineLocale;

    const loadLocale = async () => {
      try {
        await import(/* @vite-ignore */ `dayjs/locale/${mantineLocale}`);
      } catch (_error) {
        logger.warn(
          `Dayjs locale load failed for ${mantineLocale}, trying fallback`
        );
        try {
          const fallbackLocale = 'en';
          await import(/* @vite-ignore */ `dayjs/locale/${fallbackLocale}`);
        } catch {
          logger.warn('Dayjs locale fallback (en) also failed; using defaults');
        }
      }
    };

    loadLocale();
  }, [mantineLocale]);

  // DatesProvider updates reactively when settings.locale changes
  // The locale is loaded asynchronously, but DatesProvider will use it once available
  return (
    <DatesProvider settings={{ locale: mantineLocale }}>
      <AuthProvider>
        <Box
          style={{
            textAlign: 'center',
            minHeight: '100vh',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {/* Navbar is displayed on every page */}
          <Navbar />
          {/* Outlet is a placeholder for the child route's content (Login, SignUp, TripList, etc. ) */}
          <Outlet />
        </Box>
      </AuthProvider>
    </DatesProvider>
  );
};

// Root layout component that wraps the app with all providers
const RootLayout = () => {
  return (
    <LocaleProvider>
      <AppContent />
    </LocaleProvider>
  );
};

const RootRedirect = () => {
  const { isAuthenticated } = useAuth();
  logger.debug('RootRedirect', { isAuthenticated });
  if (isAuthenticated) {
    return <Navigate to="/trips" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
};

// Router configuration
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <RootRedirect />, // / redirects based on auth state
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'signup',
        element: <SignUp />,
      },
      {
        path: 's/:shareToken',
        element: <PublicItineraryRoute />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'trips',
        element: (
          <ProtectedRoute>
            <TripList />
          </ProtectedRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'trips/new',
        element: (
          <ProtectedRoute>
            <DetailsFormRoute />
          </ProtectedRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'trips/edit/:id',
        element: (
          <ProtectedRoute>
            <DetailsFormRoute />
          </ProtectedRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'trips/:id/itinerary',
        element: (
          <ProtectedRoute>
            <ItineraryOverviewRoute />
          </ProtectedRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
    ],
  },
]);
